import {
  useSearchProductVariants,
  type ProductVariantSearchResult,
} from '@/api/inventory-management/products/useSearchProductVariants';
import { useListPrices } from '@/api/rpc/hooks/accounts-receivable/useDiscounts';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@erp/shared-utils';
import { useDebouncedState } from '@react-hookz/web';
import { useGT } from 'gt-react';
import { useCallback, useState, type ReactNode } from 'react';

function VariantDropdownItem({
  variant,
  noSkuLabel,
  rightLabel,
}: {
  variant: ProductVariantSearchResult;
  noSkuLabel: string;
  rightLabel?: ReactNode;
}) {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {variant.variantSku ?? noSkuLabel}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {variant.productName}
          </span>
        </div>
        {variant.productDescription &&
          variant.productDescription !== variant.productName && (
            <span className="max-w-[400px] truncate text-xs text-muted-foreground">
              {variant.productDescription}
            </span>
          )}
      </div>
      {rightLabel && <div className="shrink-0 text-xs">{rightLabel}</div>}
    </div>
  );
}

function uniqueSkus(variants: ProductVariantSearchResult[] | undefined) {
  return Array.from(
    new Set(
      (variants ?? []).map((v) => v.variantSku).filter((s): s is string => !!s),
    ),
  );
}

function variantPickerLabels(gt: ReturnType<typeof useGT>, hasQuery: boolean) {
  return pickerLabels({
    placeholder: gt('Search by SKU or product name...', {
      context: 'Product variant autocomplete main field placeholder',
    }),
    search: gt('Type to search...', {
      context: 'Product variant autocomplete search box placeholder',
    }),
    empty: !hasQuery
      ? gt('Type to search for products', {
          context: 'Empty state before user types in product variant search',
        })
      : gt('No products found', {
          context: 'No matches for product variant search',
        }),
  });
}

const variantName = (
  variant: ProductVariantSearchResult,
  noSkuLabel: string,
  separator: string,
) => `${variant.variantSku ?? noSkuLabel}${separator}${variant.productName}`;

function ListPrice({
  sku,
  prices,
}: {
  sku: string | null | undefined;
  prices: Record<string, { listPrice: number } | null | undefined>;
}) {
  const gt = useGT();
  if (!sku) return null;
  const listPrice = prices[sku]?.listPrice ?? null;
  return listPrice == null ? (
    <span className="text-muted-foreground">
      {gt('No list price', {
        context: 'Product has no list price for the selected customer',
      })}
    </span>
  ) : (
    <span className="font-medium text-green-700 dark:text-green-400">
      {formatCurrency(listPrice, 'USD')}
    </span>
  );
}

export function ProductVariantAutocomplete({
  onVariantSelect,
  selectedVariantId,
  className,
  customerId,
  popoverModal,
}: {
  onVariantSelect: (variant: ProductVariantSearchResult | undefined) => void;
  selectedVariantId?: string;
  className?: string;
  /**
   * When provided, each result is annotated with the customer's list price
   * (or "No list price") so the user can see which products are priced for
   * that customer before selecting.
   */
  customerId?: string;
  /**
   * Set true when rendered inside a Dialog — makes the combobox Popover modal so
   * its search input still receives keystrokes (a non-modal popover inside a
   * dialog's focus trap can't be typed into, yielding no search results).
   */
  popoverModal?: boolean;
}) {
  const gt = useGT();
  const [searchQuery, _setSearchQuery] = useState('');
  const [apiSearchQuery, setApiSearchQuery] = useDebouncedState('', 150);
  const noSkuLabel = gt('No SKU', {
    context: 'Shown when a product variant has no SKU in search results',
  });

  const setSearchQuery = useCallback(
    (query: string) => {
      _setSearchQuery(query);
      setApiSearchQuery(query);
    },
    [setApiSearchQuery],
  );

  const { data: variants, isLoading } = useSearchProductVariants(
    {
      query: apiSearchQuery,
      isSellable: true,
      limit: 20,
    },
    apiSearchQuery.length > 0,
  );

  // List prices for the visible results, scoped to the selected customer.
  const skus = uniqueSkus(variants);
  const listPriceQuery = useListPrices(
    customerId && skus.length > 0 ? { customerId, skus } : null,
  );
  const priceBySku = listPriceQuery.data?.prices ?? {};

  const handleValueChange = useCallback(
    (variantId: string) => {
      const selected = variants?.find((v) => v.productVariantId === variantId);
      onVariantSelect(selected);
    },
    [variants, onVariantSelect],
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Combobox
        items={variants ?? []}
        value={selectedVariantId || null}
        onValueChange={(next) => handleValueChange(next ?? '')}
        deselectable
        disabled={isLoading && apiSearchQuery.length > 0}
        labels={variantPickerLabels(gt, apiSearchQuery.length > 0)}
        getValue={(variant) => variant.productVariantId}
        getLabel={(variant) => variantName(variant, noSkuLabel, ' - ')}
        indicator="none"
        renderItem={(variant) => (
          <VariantDropdownItem
            variant={variant}
            noSkuLabel={noSkuLabel}
            rightLabel={
              customerId ? (
                <ListPrice sku={variant.variantSku} prices={priceBySku} />
              ) : null
            }
          />
        )}
        renderValue={({ selected }) =>
          variantName(selected[0], noSkuLabel, ' — ')
        }
        // Debounced by the wrapper, not `onSearch`: the results still go
        // through the default client filter.
        search={searchQuery}
        onSearchChange={setSearchQuery}
        resetSearchOnClose={false}
        rememberItems={false}
        allowClear
        modal={popoverModal}
        contentClassName="w-full"
      />
    </div>
  );
}
